import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { HiFolder } from "react-icons/hi2";
import mahiLogo from "../../assets/images/mahi-download-logo.png";

interface BrandLayoutProps {
  children: React.ReactNode;
}

const GET_CURRENT_BRAND_NAME = gql`
  query CurrentBrandName {
    currentBrand {
      id
      name
      campaigns {
        id
      }
      contracts {
        id
        title
        campaigns {
          id
          productName
          productImageUrl
        }
      }
    }
  }
`;

const GET_CURRENT_BRAND_ACCOUNT = gql`
  query CurrentBrandAccount {
    brandMembers {
      id
      account {
        id
        email
        name
      }
    }
  }
`;

const BrandLayout: React.FC<BrandLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useQuery(GET_CURRENT_BRAND_NAME);
  const { data: accountData } = useQuery(GET_CURRENT_BRAND_ACCOUNT);
  const [expandedContractIds, setExpandedContractIds] = useState<Set<number>>(new Set());
  
  // 현재 계정 정보 (첫 번째 멤버의 계정 정보 사용)
  const currentAccount = accountData?.brandMembers?.[0]?.account;
  
  // 컨트랙트 토글 함수
  const toggleContract = (contractId: number) => {
    setExpandedContractIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contractId)) {
        newSet.delete(contractId);
      } else {
        newSet.add(contractId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    // 브랜드 대시보드 페이지 진입 시 root 요소의 max-width 제한 해제
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.maxWidth = "none";
      rootElement.style.boxShadow = "none";
    }

    // cleanup 함수: 컴포넌트 언마운트 시 원래 스타일로 복원
    return () => {
      if (rootElement) {
        rootElement.style.maxWidth = "742px";
        rootElement.style.boxShadow = "0 0 10px rgba(0, 0, 0, 0.1)";
      }
    };
  }, []);

  const ENABLE_BRAND_SETTLEMENTS = true;
  const menuItems = [
    { path: "/brand-dashboard", label: "컨트랙트", exact: true },
    ...(ENABLE_BRAND_SETTLEMENTS ? [{ path: "/brand-dashboard/settlements", label: "정산내역" }] : []),
    { path: "/brand-dashboard/members", label: "멤버 관리" },
  ];
  
  // 미션 탭이 활성화되었는지 확인
  const isMissionsActive = location.pathname === "/brand-dashboard" || location.pathname.startsWith("/brand-dashboard/campaign") || location.pathname.startsWith("/brand-dashboard/contract");

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 상단바 */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <img src={mahiLogo} alt="마하이어로" className="h-8 w-auto" />
          <span className="text-lg font-semibold text-gray-900">마하이어로</span>
        </div>
      </header>

      {/* 메인 레이아웃 */}
      <div className="flex flex-1 overflow-hidden">
        {/* 첫 번째 사이드바 */}
        <aside className="bg-white border-r border-gray-200 w-64 flex flex-col">
        {/* 블록1: 브랜드 정보 */}
        <div className="p-4 border-b border-gray-200 h-[90px] flex flex-col">
          {data?.currentBrand && (
            <div className="space-y-2">
              <div className="text-lg font-bold text-gray-900">
                {data.currentBrand.name}
              </div>
              {currentAccount && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">아이디:</span> {currentAccount.email}
                  </div>
                  {currentAccount.name && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {currentAccount.name}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 블록2: 메뉴 */}
        <nav className="p-4 flex-1">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    isActive(item.path, item.exact)
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* 두 번째 사이드바 - 미션 선택 시만 표시 */}
      {isMissionsActive && data?.currentBrand?.contracts && data.currentBrand.contracts.length > 0 && (
        <aside className="bg-white border-r border-gray-200 w-64 flex flex-col">
          {/* 블록1: 컨트랙트 패널 */}
          <div className="p-4 border-b border-gray-200 h-[90px] flex items-start">
            <h3 className="text-lg font-bold text-gray-900">컨트랙트</h3>
          </div>

          {/* 블록2: 계약 목록 */}
          <nav className="p-4 flex-1 overflow-auto">
            <ul className="space-y-3">
              {data.currentBrand.contracts.map((contract: any) => {
                const isExpanded = expandedContractIds.has(contract.id);
                return (
                  <li key={contract.id}>
                    <button
                      onClick={() => navigate(`/brand-dashboard/contract/${contract.id}`)}
                      className={`w-full text-left text-sm font-medium mb-2 px-2 py-1 rounded transition-colors hover:bg-gray-100 flex items-center gap-2 ${
                        location.pathname === `/brand-dashboard/contract/${contract.id}`
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-900"
                      }`}
                    >
                      <HiFolder className="text-blue-600 w-5 h-5" />
                      {contract.title}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleContract(contract.id);
                      }}
                      className="w-full text-left text-xs text-gray-600 px-2 py-1 rounded transition-colors hover:bg-gray-50"
                    >
                      {expandedContractIds.has(contract.id) ? "▲ 접기" : "▼ 펼치기"}
                    </button>
                    {isExpanded && contract.campaigns && contract.campaigns.length > 0 && (
                      <ul className="space-y-1">
                        {contract.campaigns.map((campaign: any) => (
                          <li key={campaign.id}>
                            <Link
                              to={`/brand-dashboard/campaign/${campaign.id}`}
                              className={`text-base font-medium hover:text-gray-900 flex items-center gap-2 pl-4 py-1 transition-colors ${
                                location.pathname === `/brand-dashboard/campaign/${campaign.id}`
                                  ? "text-blue-700"
                                  : "text-gray-600"
                              }`}
                            >
                              <span className="text-gray-400">ㄴ</span>
                              <div className="w-8 h-8 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                                {campaign.productImageUrl ? (
                                  <img
                                    src={campaign.productImageUrl}
                                    alt={campaign.productName}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                    이미지 없음
                                  </div>
                                )}
                              </div>
                              <span>{campaign.productName}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>
      )}

        {/* 메인 컨텐츠 */}
        <main className="flex-1 overflow-auto">
          <div className="p-8">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default BrandLayout;
